import {
  createRouter,
  createWebHistory,
  type RouteRecordRaw,
} from "vue-router";
import type { UserRole } from "@/types";

const routes: Array<RouteRecordRaw> = [
  // Ana sayfa - Landing page
  {
    path: "/",
    name: "Home",
    component: () => import("../views/Landing.vue"),
    meta: { template: "noside", public: true },
  },
  {
    path: "/pricing",
    name: "Pricing",
    component: () => import("../views/Pricing.vue"),
    meta: { template: "noside", public: true },
  },
  {
    path: "/about",
    name: "About",
    component: () => import("../views/About.vue"),
    meta: { template: "noside", public: true },
  },
  {
    path: "/faq",
    name: "Faq",
    component: () => import("../views/Faq.vue"),
    meta: { template: "noside", public: true },
  },
  {
    path: "/features",
    name: "Features",
    component: () => import("../views/Features.vue"),
    meta: { template: "noside", public: true },
  },
  {
    path: "/login",
    name: "Login",
    component: () => import("../views/login.vue"),
    meta: { template: "noside", public: true },
  },
  {
    path: "/forgot-password",
    name: "ForgotPassword",
    component: () => import("../views/forgot-password.vue"),
    meta: { template: "noside", public: true },
  },
  {
    path: "/logout",
    name: "Logout",
    component: () => import("../views/logout.vue"),
    meta: { template: "noside" },
  },
  {
    path: "/register",
    name: "Register",
    component: () => import("../views/register.vue"),
    meta: { template: "noside", public: true },
  },

  // ===== Sipariş & Servis (Ordering) modülü =====
  // Müşteri sipariş ekranı — masa QR'ı /t/{kod} buraya 302 yönlendirir.
  {
    path: "/order/:restaurant/:code",
    name: "CustomerOrder",
    component: () => import("../views/ordering/CustomerOrder.vue"),
    meta: { template: "noside", public: true },
  },
  // Hafif POS SPA — garson / mutfak (KDS) / kasa. Kiosk kabuğu, site chrome'u yok.
  {
    path: "/pos",
    component: () => import("../views/pos/PosShell.vue"),
    meta: { template: "noside", public: true },
    children: [
      { path: "", redirect: "/pos/waiter" },
      {
        path: "login",
        name: "PosLogin",
        component: () => import("../views/pos/PosLogin.vue"),
        meta: { template: "noside", public: true },
      },
      {
        path: "waiter",
        name: "PosWaiter",
        component: () => import("../views/pos/WaiterBoard.vue"),
        meta: { template: "noside", public: true },
      },
      {
        path: "kitchen",
        name: "PosKitchen",
        component: () => import("../views/pos/KitchenDisplay.vue"),
        meta: { template: "noside", public: true },
      },
      {
        path: "cashier",
        name: "PosCashier",
        component: () => import("../views/pos/CashierDesk.vue"),
        meta: { template: "noside", public: true },
      },
      {
        path: "setup",
        name: "PosSetup",
        component: () => import("../views/pos/KitchenSetup.vue"),
        meta: { template: "noside", public: true },
      },
      {
        path: "availability",
        name: "PosAvailability",
        component: () => import("../views/pos/MenuAvailability.vue"),
        meta: { template: "noside", public: true },
      },
      {
        path: "reports",
        name: "PosReports",
        component: () => import("../views/pos/Reports.vue"),
        meta: { template: "noside", public: true },
      },
    ],
  },

  // NOTE: /r/:shortCode and /qr/:shortCode routes removed
  // Backend now handles these with HTTP 302 redirects directly
  // See: QrRedirectController.cs

  // Restaurant Public Routes (QR ile erişim - müşteriler için)
  {
    path: "/:company/:restaurant",
    name: "Restaurant",
    component: () => import("../views/restaurant.vue"),
    meta: { template: "panel", public: true },
  },
  {
    path: "/:company/:restaurant/:category",
    name: "Category",
    component: () => import("../views/category.vue"),
    meta: { template: "panel", public: true },
  },

  // Admin Login (Separate page - public but hidden)
  {
    path: "/admin/login",
    name: "AdminLogin",
    component: () => import("../views/admin/AdminLogin.vue"),
    meta: { template: "noside", public: true },
  },

  // Admin Panel (LoginUser - Highest authority)
  {
    path: "/admin",
    component: () => import("../layouts/AdminLayout.vue"),
    meta: { requiresAuth: true, roles: ["Admin"] },
    children: [
      {
        path: "",
        redirect: "/admin/dashboard",
      },
      {
        path: "dashboard",
        name: "AdminDashboard",
        component: () => import("../views/admin/Dashboard.vue"),
        meta: { requiresAuth: true, roles: ["Admin"], title: "Dashboard" },
      },
      {
        path: "companies",
        name: "AdminCompanies",
        component: () => import("../views/admin/Companies.vue"),
        meta: { requiresAuth: true, roles: ["Admin"], title: "Şirketler" },
      },
      {
        path: "restaurants",
        name: "AdminRestaurants",
        component: () => import("../views/admin/Restaurants.vue"),
        meta: { requiresAuth: true, roles: ["Admin"], title: "Restoranlar" },
      },
      {
        path: "managers",
        name: "AdminManagers",
        component: () => import("../views/admin/Managers.vue"),
        meta: { requiresAuth: true, roles: ["Admin"], title: "Yöneticiler" },
      },
      {
        path: "statistics",
        name: "AdminStatistics",
        component: () => import("../views/admin/Statistics.vue"),
        meta: { requiresAuth: true, roles: ["Admin"], title: "İstatistikler" },
      },
      {
        path: "test-data",
        name: "AdminTestData",
        component: () => import("../views/admin/TestData.vue"),
        meta: { requiresAuth: true, roles: ["Admin"], title: "Test Verileri" },
      },
      {
        path: "pending-managers",
        name: "AdminPendingManagers",
        component: () => import("../views/admin/PendingManagers.vue"),
        meta: { requiresAuth: true, roles: ["Admin"], title: "Onay Bekleyen Yöneticiler" },
      },
      {
        path: "pending-restaurants",
        name: "AdminPendingRestaurants",
        component: () => import("../views/admin/PendingRestaurants.vue"),
        meta: { requiresAuth: true, roles: ["Admin"], title: "Onay Bekleyen Restoranlar" },
      },
      {
        path: "subscriptions",
        name: "AdminSubscriptions",
        component: () => import("../views/admin/Subscriptions.vue"),
        meta: { requiresAuth: true, roles: ["Admin"], title: "Abonelik Yönetimi" },
      },
      {
        path: "invoices",
        name: "AdminInvoices",
        component: () => import("../views/admin/Invoices.vue"),
        meta: { requiresAuth: true, roles: ["Admin"], title: "Fatura Yönetimi" },
      },
      {
        path: "packages",
        name: "AdminPackages",
        component: () => import("../views/admin/Packages.vue"),
        meta: { requiresAuth: true, roles: ["Admin"], title: "Abonelik Paketleri" },
      },
      {
        path: "partners",
        name: "AdminPartners",
        component: () => import("../views/admin/Partners.vue"),
        meta: { requiresAuth: true, roles: ["Admin"], title: "İş Ortakları" },
      },
    ],
  },

  // İş Ortağı Login (Separate page — mevcut admin/manager login'lerinden tamamen ayrı)
  {
    path: "/partner/login",
    name: "PartnerLogin",
    component: () => import("../views/partner/PartnerLogin.vue"),
    meta: { template: "noside", public: true },
  },

  // İş Ortağı Paneli — kendi ayrı localStorage anahtarlarını kullanır (partnerAccessToken/partnerUser),
  // bu yüzden genel requiresAuth/roles mekanizmasını DEĞİL, ayrı requiresPartnerAuth guard'ını kullanır.
  {
    path: "/partner",
    component: () => import("../layouts/PartnerLayout.vue"),
    meta: { requiresPartnerAuth: true },
    children: [
      {
        path: "",
        redirect: "/partner/dashboard",
      },
      {
        path: "dashboard",
        name: "PartnerDashboard",
        component: () => import("../views/partner/Dashboard.vue"),
        meta: { requiresPartnerAuth: true, title: "Müşterilerim" },
      },
    ],
  },

  // Manager Panel (User/Manager - Medium authority)
  {
    path: "/management",
    component: () => import("../layouts/ManagerLayout.vue"),
    meta: { requiresAuth: true, roles: ["Manager", "User"] },
    children: [
      {
        path: "",
        redirect: "/management/dashboard",
      },
      {
        path: "dashboard",
        name: "ManagementDashboard",
        component: () => import("../views/management/Dashboard.vue"),
        meta: { requiresAuth: true, roles: ["Manager", "User"], title: "Dashboard" },
      },
      {
        path: "info",
        name: "ManagementInfo",
        component: () => import("../views/management/RestaurantInfo.vue"),
        meta: { requiresAuth: true, roles: ["Manager", "User"], title: "Restoran Bilgileri" },
      },
      {
        path: "categories",
        name: "ManagementCategories",
        component: () => import("../views/management/Categories.vue"),
        meta: { requiresAuth: true, roles: ["Manager", "User"], title: "Kategoriler" },
      },
      {
        path: "products",
        name: "ManagementProducts",
        component: () => import("../views/management/Products.vue"),
        meta: { requiresAuth: true, roles: ["Manager", "User"], title: "Ürünler" },
      },
      {
        path: "staff",
        name: "ManagementStaff",
        component: () => import("../views/management/Staff.vue"),
        meta: { requiresAuth: true, roles: ["Manager", "User"], title: "Personel Yönetimi" },
      },
      {
        path: "tables",
        name: "ManagementTables",
        component: () => import("../views/management/Tables.vue"),
        meta: { requiresAuth: true, roles: ["Manager", "User"], title: "Masa Yönetimi" },
      },
      {
        path: "announcements",
        name: "ManagementAnnouncements",
        component: () => import("../views/management/Announcements.vue"),
        meta: { requiresAuth: true, roles: ["Manager", "User"], title: "Duyurular" },
      },
      {
        path: "currencies",
        name: "ManagementCurrencies",
        component: () => import("../views/management/Currencies.vue"),
        meta: { requiresAuth: true, roles: ["Manager", "User"], title: "Para Birimleri" },
      },
      {
        path: "statistics",
        name: "ManagementStatistics",
        component: () => import("../views/management/Statistics.vue"),
        meta: { requiresAuth: true, roles: ["Manager", "User"], title: "İstatistikler" },
      },
      {
        path: "qr-codes",
        name: "ManagementQrCodes",
        component: () => import("../views/management/QrCodes.vue"),
        meta: { requiresAuth: true, roles: ["Manager", "User"], title: "QR Kodlar" },
      },
      {
        path: "discounts",
        name: "ManagementDiscounts",
        component: () => import("../views/management/Discounts.vue"),
        meta: { requiresAuth: true, roles: ["Manager", "User"], title: "İndirimler" },
      },
      {
        path: "global-discount",
        name: "ManagementGlobalDiscount",
        component: () => import("../views/management/GlobalDiscount.vue"),
        meta: { requiresAuth: true, roles: ["Manager", "User"], title: "Global İndirim" },
      },
      {
        path: "theme",
        name: "ManagementTheme",
        component: () => import("../views/management/ThemeSettings.vue"),
        meta: { requiresAuth: true, roles: ["Manager", "User"], title: "Tema Ayarları" },
      },
      {
        path: "subscription",
        name: "ManagementSubscription",
        component: () => import("../views/management/Subscription.vue"),
        meta: { requiresAuth: true, roles: ["Manager", "User"], title: "Abonelik Bilgileri", allowExpired: true },
      },
      {
        path: "invoices",
        name: "ManagementInvoices",
        component: () => import("../views/management/Invoices.vue"),
        meta: { requiresAuth: true, roles: ["Manager", "User"], title: "Faturalarım", allowExpired: true },
      },
      {
        path: "profile",
        name: "ManagementProfile",
        component: () => import("../views/management/Profile.vue"),
        meta: { requiresAuth: true, roles: ["Manager", "User"], title: "Profil Ayarları", allowExpired: true },
      },
      {
        path: "payment",
        name: "ManagementPayment",
        component: () => import("../views/management/Payment.vue"),
        meta: { requiresAuth: true, roles: ["Manager", "User"], title: "Ödeme", allowExpired: true },
      },
    ],
  },
  // Payment Result Pages (outside of management layout)
  {
    path: "/payment/success",
    name: "PaymentSuccess",
    component: () => import("../views/PaymentSuccess.vue"),
    meta: { requiresAuth: true, roles: ["Manager", "User"], template: "noside" },
  },
  {
    path: "/payment/fail",
    name: "PaymentFail",
    component: () => import("../views/PaymentFail.vue"),
    meta: { requiresAuth: true, roles: ["Manager", "User"], template: "noside" },
  },
  {
    path: "/payment/3d-success",
    name: "PaymentSuccess3D",
    component: () => import("../views/PaymentSuccess3D.vue"),
    meta: { requiresAuth: true, roles: ["Manager", "User"], template: "noside" },
  },
  {
    path: "/payment/3d-fail",
    name: "PaymentFail3D",
    component: () => import("../views/PaymentFail3D.vue"),
    meta: { requiresAuth: true, roles: ["Manager", "User"], template: "noside" },
  },

  // 404 Page
  {
    path: "/:pathMatch(.*)*",
    name: "PageNotFound",
    component: () => import("../views/pageNotFound.vue"),
    meta: { template: "noside", public: true },
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    return savedPosition || { top: 0 };
  },
});

// Navigation Guard
router.beforeEach((to, from, next) => {
  const isPublic = to.meta.public === true;
  const requiresAuth = to.meta.requiresAuth === true;
  const requiresPartnerAuth = to.meta.requiresPartnerAuth === true;
  const requiredRoles = to.meta.roles as UserRole[] | undefined;

  // Public route - allow access
  if (isPublic) {
    next();
    return;
  }

  // İş Ortağı rotaları: AYRI localStorage anahtarları (partnerAccessToken/partnerUser) —
  // admin/manager'ın genel accessToken/loginUser guard'ını hiç kullanmaz, aynı tarayıcıda
  // açık bir admin/manager oturumuyla çakışmaz.
  if (requiresPartnerAuth) {
    const partnerToken = localStorage.getItem("partnerAccessToken");
    const partnerUserStr = localStorage.getItem("partnerUser");
    if (!partnerToken || !partnerUserStr) {
      next({ name: "PartnerLogin" });
      return;
    }
    next();
    return;
  }

  // Route requires authentication - check localStorage
  if (requiresAuth) {
    const accessToken = localStorage.getItem("accessToken");
    const userStr = localStorage.getItem("loginUser");
    const isAuthenticated = !!accessToken && !!userStr;

    // Determine if this is an admin route
    const isAdminRoute = to.path.startsWith('/admin');

    if (!isAuthenticated) {
      // Redirect to appropriate login page
      if (isAdminRoute) {
        next({ name: "AdminLogin" });
      } else {
        next({
          name: "Login",
          query: { redirect: to.fullPath },
        });
      }
      return;
    }

    // Check if user has required role
    if (requiredRoles && requiredRoles.length > 0) {
      try {
        const user = JSON.parse(userStr);
        const userRole = user?.role || "Admin";
        // Case-insensitive role check
        const hasRole = requiredRoles.some(
          role => role.toLowerCase() === userRole.toLowerCase()
        );
        if (!hasRole) {
          // Redirect to appropriate login page based on route
          if (isAdminRoute) {
            next({ name: "AdminLogin" });
          } else {
            next({ name: "Login" });
          }
          return;
        }
      } catch (e) {
        if (isAdminRoute) {
          next({ name: "AdminLogin" });
        } else {
          next({ name: "Login" });
        }
        return;
      }
    }
  }

  next();
});

export default router;
